// src/pages/Dashboard/sections/network-tabs/WithdrawalHistoryModal.jsx
import React, { useState, useEffect } from 'react';
import { getUserWithdrawals, getWithdrawalDetails } from '../../../../services/withdrawalService';

/**
 * Withdrawal History Modal Component
 * Displays user's withdrawal history and details
 */
const WithdrawalHistoryModal = ({ isOpen, onClose, wallet, formatNumber, formatDate }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [withdrawalDetails, setWithdrawalDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Fetch withdrawal history when component mounts
  useEffect(() => {
    if (isOpen && wallet.connected) {
      fetchWithdrawals(statusFilter, currentPage);
    }
  }, [isOpen, wallet.connected, statusFilter, currentPage]);
  
  // Fetch withdrawal history from API
  const fetchWithdrawals = async (status = 'all', page = 1) => {
    if (!wallet.connected) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Call API to get withdrawal history
      const result = await getUserWithdrawals(wallet, {
        status: status === 'all' ? null : status,
        page: page,
        page_size: 10
      });
      
      setWithdrawals(result.withdrawals || []);
      setTotalPages(result.pagination?.total_pages || 1);
    } catch (err) {
      console.error('Error fetching withdrawal history:', err);
      setError('Failed to load withdrawal history');
      setWithdrawals([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch withdrawal details
  const fetchWithdrawalDetails = async (withdrawalId) => {
    if (!wallet.connected || !withdrawalId) return;
    
    setIsLoadingDetails(true);
    
    try {
      const details = await getWithdrawalDetails(wallet, withdrawalId);
      setWithdrawalDetails(details);
      setShowDetails(true);
    } catch (err) {
      console.error('Error fetching withdrawal details:', err);
      setError(`Failed to load details for withdrawal ${withdrawalId}`);
    } finally {
      setIsLoadingDetails(false);
    }
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };
  
  // Show withdrawal details
  const handleShowDetails = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    fetchWithdrawalDetails(withdrawal.withdrawal_id);
  };
  
  // Close details view
  const handleCloseDetails = () => {
    setShowDetails(false);
    setWithdrawalDetails(null);
    setSelectedWithdrawal(null);
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'pending':
        return 'status-pending';
      case 'processing':
        return 'status-processing';
      case 'rejected':
        return 'status-rejected';
      case 'failed':
        return 'status-failed';
      default:
        return '';
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="withdrawal-history-modal">
        <div className="modal-header">
          <h3>Withdrawal History</h3>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        {showDetails ? (
          <div className="withdrawal-details">
            <button className="back-btn" onClick={handleCloseDetails}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Withdrawals
            </button>
            
            {isLoadingDetails ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading withdrawal details...</p>
              </div>
            ) : withdrawalDetails ? (
              <div className="details-container">
                <div className="details-header">
                  <div className="withdrawal-id">{withdrawalDetails.withdrawal_id}</div>
                  <div className={`status-badge ${getStatusBadgeClass(withdrawalDetails.status)}`}>
                    {withdrawalDetails.status?.charAt(0).toUpperCase() + withdrawalDetails.status?.slice(1)}
                  </div>
                </div>
                
                <div className="details-grid">
                  <div className="detail-item">
                    <div className="detail-label">Amount:</div>
                    <div className="detail-value">{formatNumber(withdrawalDetails.amount, 2)} USDT</div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Fee:</div>
                    <div className="detail-value">{formatNumber(withdrawalDetails.fee_amount, 2)} USDT</div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Net Amount:</div>
                    <div className="detail-value">{formatNumber(withdrawalDetails.net_amount, 2)} USDT</div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Wallet Address:</div>
                    <div className="detail-value wallet-address">{withdrawalDetails.wallet_address}</div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Requested At:</div>
                    <div className="detail-value">{formatDate(withdrawalDetails.requested_at)}</div>
                  </div>
                  
                  {withdrawalDetails.approved_at && (
                    <div className="detail-item">
                      <div className="detail-label">Approved At:</div>
                      <div className="detail-value">{formatDate(withdrawalDetails.approved_at)}</div>
                    </div>
                  )}
                  
                  {withdrawalDetails.processed_at && (
                    <div className="detail-item">
                      <div className="detail-label">Processed At:</div>
                      <div className="detail-value">{formatDate(withdrawalDetails.processed_at)}</div>
                    </div>
                  )}
                </div>
                
                {withdrawalDetails.transaction_hash && (
                  <div className="transaction-details">
                    <div className="detail-label">Transaction Hash:</div>
                    <div className="transaction-hash">
                      <div className="hash-text">{withdrawalDetails.transaction_hash}</div>
                      <button 
                        className="copy-btn"
                        onClick={() => navigator.clipboard.writeText(withdrawalDetails.transaction_hash)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 4v16h12V4H8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 4V2H4v16h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      
                      <a 
                        href={`https://bscscan.com/tx/${withdrawalDetails.transaction_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-btn"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        View on BSCscan
                      </a>
                    </div>
                  </div>
                )}
                
                {withdrawalDetails.rejection_reason && (
                  <div className="rejection-reason">
                    <div className="detail-label">Rejection Reason:</div>
                    <div className="reason-text">{withdrawalDetails.rejection_reason}</div>
                  </div>
                )}
                
                {/* Withdrawal Timeline */}
                {withdrawalDetails.logs && withdrawalDetails.logs.length > 0 && (
                  <div className="withdrawal-timeline">
                    <h4>Withdrawal Timeline</h4>
                    <div className="timeline">
                      {withdrawalDetails.logs.map((log, index) => (
                        <div key={index} className="timeline-item">
                          <div className="timeline-icon">
                            {log.action === 'creation' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : log.action === 'approval' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : log.action === 'rejection' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : log.action === 'processing' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : log.action === 'completion' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <div className="timeline-content">
                            <div className="timeline-time">{formatDate(log.timestamp)}</div>
                            <div className="timeline-text">{log.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="error-message">
                <p>Failed to load withdrawal details. Please try again.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="withdrawal-list-container">
            <div className="filter-controls">
              <div className="status-filter">
                <label>Filter by Status:</label>
                <select value={statusFilter} onChange={handleStatusFilterChange}>
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
            
            {isLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading withdrawals...</p>
              </div>
            ) : error ? (
              <div className="error-message">
                <p>{error}</p>
              </div>
            ) : withdrawals.length > 0 ? (
              <>
                <div className="withdrawals-table-container">
                  <table className="withdrawals-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawals.map((withdrawal) => (
                        <tr key={withdrawal.withdrawal_id}>
                          <td className="id-cell">{withdrawal.withdrawal_id}</td>
                          <td>{formatDate(withdrawal.requested_at)}</td>
                          <td className="amount-cell">{formatNumber(withdrawal.amount, 2)} USDT</td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(withdrawal.status)}`}>
                              {withdrawal.status?.charAt(0).toUpperCase() + withdrawal.status?.slice(1)}
                            </span>
                          </td>
                          <td>
                            <button 
                              className="details-btn"
                              onClick={() => handleShowDetails(withdrawal)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    
                    <span className="pagination-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 17l5-5-5-5M6 17l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-withdrawals">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>No withdrawal requests found.</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(3px);
        }
        
        .withdrawal-history-modal {
          background: var(--darker, #111827);
          border-radius: 16px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .modal-header h3 {
          margin: 0;
          font-size: 1.3rem;
        }
        
        .close-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: var(--text, #ffffff);
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .withdrawal-list-container,
        .withdrawal-details {
          padding: 1.5rem;
        }
        
        .filter-controls {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }
        
        .status-filter {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .status-filter label {
          font-size: 0.9rem;
          color: var(--text-secondary, rgba(255, 255, 255, 0.7));
        }
        
        .status-filter select {
          padding: 0.5rem 1rem;
          background: rgba(15, 23, 42, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: var(--text, #ffffff);
          font-size: 0.9rem;
        }
        
        .withdrawals-table-container {
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(15, 23, 42, 0.2);
          overflow: hidden;
          margin-bottom: 1.5rem;
        }
        
        .withdrawals-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .withdrawals-table th {
          background: rgba(15, 23, 42, 0.5);
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: var(--text, #ffffff);
          font-size: 0.9rem;
        }
        
        .withdrawals-table td {
          padding: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          color: var(--text-secondary, rgba(255, 255, 255, 0.7));
          font-size: 0.9rem;
        }
        
        .withdrawals-table tbody tr {
          transition: background-color 0.2s ease;
        }
        
        .withdrawals-table tr:hover {
          background: rgba(255, 255, 255, 0.05);
        }
        
        .id-cell {
          font-family: monospace;
          font-size: 0.85rem;
          color: var(--primary, #6366f1);
        }
        
        .amount-cell {
          font-weight: 600;
          color: var(--text, #ffffff);
        }
        
        .status-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        
        .status-completed {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success, #10b981);
        }
        
        .status-pending {
          background: rgba(245, 158, 11, 0.1);
          color: var(--warning, #f59e0b);
        }
        
        .status-processing {
          background: rgba(59, 130, 246, 0.1);
          color: var(--info, #3b82f6);
        }
        
        .status-rejected {
          background: rgba(239, 68, 68, 0.1);
          color: var(--error, #ef4444);
        }
        
        .status-failed {
          background: rgba(239, 68, 68, 0.1);
          color: var(--error, #ef4444);
        }
        
        .details-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          background: rgba(99, 102, 241, 0.1);
          border: none;
          border-radius: 8px;
          color: var(--primary, #6366f1);
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .details-btn:hover {
          background: rgba(99, 102, 241, 0.2);
        }
        
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        
        .pagination-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: rgba(15, 23, 42, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          color: var(--text, #ffffff);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .pagination-btn:hover:not(:disabled) {
          background: rgba(99, 102, 241, 0.1);
          border-color: var(--primary, #6366f1);
          color: var(--primary, #6366f1);
        }
        
        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .pagination-info {
          font-size: 0.9rem;
          color: var(--text-secondary, rgba(255, 255, 255, 0.7));
        }
        
        .no-withdrawals {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 3rem 1rem;
          text-align: center;
          color: var(--text-secondary, rgba(255, 255, 255, 0.7));
        }
        
        .no-withdrawals svg {
          margin-bottom: 1.5rem;
          opacity: 0.7;
        }
        
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 3rem 1rem;
          gap: 1.5rem;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          border-top-color: var(--primary, #6366f1);
          animation: spin 1s infinite linear;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .error-message {
          padding: 1.5rem;
          text-align: center;
          color: var(--error, #ef4444);
        }
        
        /* Withdrawal Details Styles */
        .back-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(15, 23, 42, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: var(--text, #ffffff);
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 1.5rem;
        }
        
        .back-btn:hover {
          background: rgba(15, 23, 42, 0.4);
          border-color: rgba(255, 255, 255, 0.2);
        }
        
        .details-container {
          background: rgba(15, 23, 42, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 1.5rem;
        }
        
        .details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .withdrawal-id {
          font-family: monospace;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--primary, #6366f1);
        }
        
        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .detail-label {
          font-size: 0.85rem;
          color: var(--text-secondary, rgba(255, 255, 255, 0.7));
        }
        
        .detail-value {
          font-weight: 500;
          font-size: 0.95rem;
        }
        
        .wallet-address {
          font-family: monospace;
          font-size: 0.85rem;
          word-break: break-all;
        }
        
        .transaction-details {
          background: rgba(15, 23, 42, 0.4);
          border-radius: 12px;
          padding: 1.25rem;
          margin-top: 1.5rem;
        }
        
        .transaction-hash {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 0.5rem;
          background: rgba(15, 23, 42, 0.6);
          border-radius: 8px;
          padding: 0.75rem;
          overflow-x: auto;
        }
        
        .hash-text {
          font-family: monospace;
          font-size: 0.85rem;
          flex: 1;
          overflow-x: auto;
          white-space: nowrap;
        }
        
        .copy-btn,
        .view-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 6px;
          color: var(--text, #ffffff);
          padding: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .copy-btn:hover,
        .view-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .view-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(99, 102, 241, 0.1);
          color: var(--primary, #6366f1);
          padding: 0.5rem 0.75rem;
          text-decoration: none;
          font-size: 0.85rem;
        }
        
        .view-btn:hover {
          background: rgba(99, 102, 241, 0.2);
        }
        
        .rejection-reason {
          background: rgba(239, 68, 68, 0.1);
          border-radius: 12px;
          padding: 1.25rem;
          margin-top: 1.5rem;
          border-left: 3px solid var(--error, #ef4444);
        }
        
        .reason-text {
          margin-top: 0.5rem;
          font-style: italic;
        }
        
        .withdrawal-timeline {
          margin-top: 2rem;
        }
        
        .withdrawal-timeline h4 {
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }
        
        .timeline {
          position: relative;
          padding-left: 2rem;
        }
        
        .timeline::before {
          content: '';
          position: absolute;
          top: 0;
          left: 8px;
          height: 100%;
          width: 2px;
          background: rgba(255, 255, 255, 0.1);
        }
        
        .timeline-item {
          position: relative;
          padding-bottom: 1.5rem;
        }
        
        .timeline-icon {
          position: absolute;
          left: -2rem;
          top: 0;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          color: var(--primary, #6366f1);
        }
        
        .timeline-content {
          background: rgba(15, 23, 42, 0.3);
          border-radius: 8px;
          padding: 1rem;
        }
        
        .timeline-time {
          font-size: 0.85rem;
          color: var(--text-secondary, rgba(255, 255, 255, 0.7));
          margin-bottom: 0.5rem;
        }
        
        .timeline-text {
          font-size: 0.95rem;
        }
        
        @media (max-width: 768px) {
          .withdrawal-history-modal {
            width: 95%;
            max-height: 80vh;
          }
          
          .details-grid {
            grid-template-columns: 1fr;
          }
          
          .status-filter {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .status-filter select {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default WithdrawalHistoryModal;
